import { Component, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { CommunityForumService } from '../../../../core/services/community-forum.service';
import { AuthService } from '../../../../core/auth/services/auth.service';
import { CommunityDiscussion, CommunitySpace } from '../../../../core/models';
import { LocalizedDatePipe } from '../../../../shared/pipes/localized-date.pipe';
import { ScrollReveal } from '../../../../shared/directives/scroll-reveal';
import { ChannelStyle, styleForCode } from './communities.data';

/**
 * UC-16/17 "Communautés" : liste des espaces du forum réel (communication-service,
 * CommunityForumController /communications/community/spaces). "Rejoindre" une communauté =
 * suivre son espace (POST/DELETE .../spaces/{id}/follow) : pas d'adhésion à valider, l'action est
 * immédiate côté backend. Tout le module exige un JWT (aucune route publique), d'où la coupure
 * anonyme/connecté ci-dessous.
 */
@Component({
  selector: 'app-communities',
  imports: [RouterLink, ScrollReveal, TranslatePipe, LocalizedDatePipe],
  templateUrl: './communities.html',
})
export class Communities {
  private readonly forum = inject(CommunityForumService);
  private readonly auth = inject(AuthService);

  readonly isAuthenticated = this.auth.isAuthenticated;

  readonly loading = signal(true);
  readonly error = signal(false);
  readonly spaces = signal<CommunitySpace[]>([]);
  readonly trending = signal<CommunityDiscussion[]>([]);
  private readonly followedIds = signal<ReadonlySet<string>>(new Set());
  private readonly followPendingIds = signal<ReadonlySet<string>>(new Set());

  constructor() {
    if (!this.auth.isAuthenticated()) {
      this.loading.set(false);
      return;
    }
    this.load();
  }

  private load(): void {
    this.loading.set(true);
    this.error.set(false);
    this.forum.spaces().subscribe({
      next: (page) => {
        this.spaces.set(page.content);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.error.set(true);
      },
    });
    this.forum.followedSpaces().subscribe({
      next: (spaces) => this.followedIds.set(new Set(spaces.map((s) => s.id))),
      error: () => undefined,
    });
    this.forum.trending(0, 5).subscribe({
      next: (page) => this.trending.set(page.content),
      error: () => undefined,
    });
  }

  isFollowed(spaceId: string): boolean {
    return this.followedIds().has(spaceId);
  }

  isFollowPending(spaceId: string): boolean {
    return this.followPendingIds().has(spaceId);
  }

  toggleFollow(space: CommunitySpace, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.isFollowPending(space.id)) return;
    this.followPendingIds.update((ids) => new Set(ids).add(space.id));
    const wasFollowed = this.isFollowed(space.id);
    const request = wasFollowed ? this.forum.unfollowSpace(space.id) : this.forum.followSpace(space.id);
    request.subscribe({
      next: () =>
        this.followedIds.update((ids) => {
          const next = new Set(ids);
          wasFollowed ? next.delete(space.id) : next.add(space.id);
          return next;
        }),
      complete: () =>
        this.followPendingIds.update((ids) => {
          const next = new Set(ids);
          next.delete(space.id);
          return next;
        }),
    });
  }

  styleFor(code: string): ChannelStyle {
    return styleForCode(code);
  }
}
